import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Tab state persisted in the URL (?<key>=<value>) so views are
 * refresh-safe, back-button-friendly and deep-linkable.
 * Drop-in replacement for useState<string | undefined>.
 *
 * When `allowedValues` is provided, unknown/stale URL values fall back to
 * `defaultValue` instead of leaking through and rendering a blank page.
 */
const useTabParam = (
  key: string,
  defaultValue?: string,
  allowedValues?: readonly string[],
): [string | undefined, (value: string | undefined) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value =
    raw !== null && (!allowedValues || allowedValues.includes(raw))
      ? raw
      : defaultValue;

  const setValue = useCallback(
    (next: string | undefined) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === undefined || next === defaultValue) {
            params.delete(key);
          } else {
            params.set(key, next);
          }
          return params;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams],
  );

  return [value, setValue];
};

export default useTabParam;
